import React, { useState, useEffect } from 'react';
import { ExternalLink, Radio, Video } from 'lucide-react';
import { detectStreamUrl } from '../../lib/streamDetect';

const trackView = (gameId, kind) => {
  if (!gameId) return;
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const url = `${apiBase}/api/stream-views/track`;
  const payload = JSON.stringify({ game_id: gameId, kind });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true });
    }
  } catch (e) { /* swallow */ }
};

/**
 * Parent-facing live-video card.
 * Modes: active=true → embedded player · active=false → compact "Replay" link
 */
export default function LiveStreamPlayer({ streamUrl, gameId, active = true, gameLabel = 'Live Game' }) {
  const detected = detectStreamUrl(streamUrl);
  const [expanded, setExpanded] = useState(true);

  // Fire 'embed_load' beacon once when the iframe goes live for this view
  useEffect(() => {
    if (active && detected && detected.embed_url && expanded && gameId) {
      trackView(gameId, 'embed_load');
    }
  }, [active, expanded, gameId, detected]);

  if (!detected) return null;

  // ---- OFFLINE / REPLAY MODE ----
  if (!active) {
    return (
      <a
        href={detected.watch_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 bg-stone-800 border border-stone-700 hover:border-stone-500 rounded-xl px-3 py-2.5 transition group"
        data-testid="live-stream-replay-card"
        id="live-feed"
      >
        <div className="w-9 h-9 rounded-full bg-stone-700 flex items-center justify-center shrink-0 group-hover:bg-stone-600 transition">
          <Video size={16} className="text-stone-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-stone-200 text-xs font-bold uppercase tracking-wider">Replay available</p>
          <p className="text-stone-400 text-[11px] truncate">Watch on {detected.label}</p>
        </div>
        <ExternalLink size={14} className="text-stone-400 shrink-0" />
      </a>
    );
  }

  // ---- LIVE MODE ----
  return (
    <div
      className="bg-stone-950 border-2 border-red-500 rounded-2xl overflow-hidden shadow-2xl"
      data-testid="live-stream-card"
      id="live-feed"
    >
      <div className="bg-gradient-to-r from-red-600 to-red-700 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <span className="flex items-center gap-1.5 bg-red-900/40 backdrop-blur-sm px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            Live · {detected.label}
          </span>
          <span className="text-xs font-semibold truncate max-w-[160px]" title={gameLabel}>
            {gameLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={detected.watch_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-white/90 hover:text-white text-[10px] font-semibold"
            data-testid="live-stream-open-external"
          >
            <ExternalLink size={11} /> Open
          </a>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-white/80 hover:text-white text-[11px] font-bold uppercase tracking-wider"
            data-testid="live-stream-toggle"
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="bg-black aspect-video w-full">
          {detected.embed_url ? (
            <iframe
              src={detected.embed_url}
              title={`Live stream: ${detected.label}`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              data-testid="live-stream-iframe"
            />
          ) : (
            // Unknown source — show a generic "Watch on …" CTA
            <div className="w-full h-full flex flex-col items-center justify-center text-stone-300 p-6 text-center">
              <Video size={32} className="mb-2 text-red-400" />
              <p className="text-sm font-semibold mb-3">Live stream available</p>
              <a
                href={detected.watch_url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-500 hover:bg-red-400 text-white font-bold px-5 py-2 rounded-full inline-flex items-center gap-2 text-sm"
                data-testid="live-stream-cta"
              >
                <Radio size={14} /> Watch the game
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
